import React, { useState, useEffect } from "react";
import "../styles/SeatAllotment.css";
import {
  getUnAllottedStudentDetails,
  AllotSeatToStudent,
  getStudentDetails,
} from "../api/StudentDetails";
import { Student } from "../types";
import { useWebSocketRFID } from "../hooks/useWebSocketRFID";
import { getUnAllotedSeat } from "../api/Seats";

const SeatAllotment: React.FC = () => {
  const [unallottedStudents, setUnallottedStudents] = useState<Student[]>([]);
  const [allottedStudents, setAllottedStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [listUnAllocatedSeat, setListUnAllocatedSeat] = useState<SeatModel[]>(
    []
  );

  const [formData, setFormData] = useState({
    studentId: "",
    seatNumber: "",
    rfidCode: "",
  });

  const [errors, setErrors] = useState({
    studentId: "",
    seatNumber: "",
    rfidCode: "",
  });

  // WebSocket connection for RFID reading
  const { isConnected: wsConnected, error: wsError } = useWebSocketRFID({
    url: "wss://nodewebsocket.omsaireadingroom.in",
    enabled: true,
    onMessage: (rfid) => {
      console.log("RFID received from WebSocket in SeatAllotment:", rfid);
      // Auto-populate RFID field when data is received
      setFormData((prev) => {
        console.log(
          "Updating formData with RFID:",
          rfid,
          "Previous value:",
          prev.rfidCode
        );
        return {
          ...prev,
          rfidCode: rfid,
        };
      });
      // Clear RFID error if any
      setErrors((prev) => ({
        ...prev,
        rfidCode: "",
      }));
    },
  });

  useEffect(() => {
    fetchStudents();
    getUnAllotedSeat().then((res) => {
      setListUnAllocatedSeat(res.data || []);
    });
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const [unallottedResponse, allottedResponse] = await Promise.all([
        getUnAllottedStudentDetails(),
        getStudentDetails(),
      ]);
      setUnallottedStudents(unallottedResponse.data || []);
      setAllottedStudents(allottedResponse.data || []);
    } catch (error) {
      console.error("Error fetching students:", error);
      setErrorMessage("Failed to load students. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getOccupiedSeats = (): number[] => {
    return allottedStudents
      .map((student) => student.seatAllotmentId)
      .filter((id) => id > 0);
  };

  const validateForm = (): boolean => {
    const newErrors = {
      studentId: "",
      seatNumber: "",
      rfidCode: "",
    };

    if (!formData.studentId) {
      newErrors.studentId = "Please select a student";
    }

    if (!formData.seatNumber) {
      newErrors.seatNumber = "Seat number is required";
    } else {
      const seatNum = parseInt(formData.seatNumber);

      const occupiedSeats = getOccupiedSeats();
      if (occupiedSeats.includes(seatNum)) {
        newErrors.seatNumber = `Seat ${seatNum} is already occupied`;
      }
    }

    if (!formData.rfidCode.trim()) {
      newErrors.rfidCode = "RFID code is required";
    } else if (formData.rfidCode.trim().length < 3) {
      newErrors.rfidCode = "RFID code must be at least 3 characters";
    }

    setErrors(newErrors);
    return !newErrors.studentId && !newErrors.seatNumber && !newErrors.rfidCode;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    // Clear messages
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleStudentSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const studentId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      studentId,
    }));
    setErrors((prev) => ({
      ...prev,
      studentId: "",
    }));
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const selectedStudent = unallottedStudents.find(
      (s) =>
        `${s.firstName} ${s.lastName} (${s.emailId})` === formData.studentId
    );

    if (!selectedStudent) {
      setErrorMessage("Selected student not found");
      return;
    }

    try {
      setSubmitting(true);
      setErrorMessage("");
      setSuccessMessage("");

      const studentToAllot: Student = {
        ...selectedStudent,
        seatAllotmentId: parseInt(formData.seatNumber),
        rfIdentificationCode: formData.rfidCode.trim(),
        seatNumber: formData.seatNumber,
      };

      const result = await AllotSeatToStudent(studentToAllot);

      if (result.data == "Allotment Is Successful") {
        setSuccessMessage(
          `Successfully allotted Seat A-${formData.seatNumber} and RFID code to ${selectedStudent.firstName} ${selectedStudent.lastName}`
        );

        // Reset form
        setFormData({
          studentId: "",
          seatNumber: "",
          rfidCode: "",
        });

        // Refresh student lists
        await fetchStudents();
      } else {
        setErrorMessage("Failed to allot seat. Please try again.");
        throw new Error("Allotment failed");
      }
    } catch (error) {
      console.error("Error allotting seat:", error);
      setErrorMessage("Failed to allot seat. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      studentId: "",
      seatNumber: "",
      rfidCode: "",
    });
    setErrors({
      studentId: "",
      seatNumber: "",
      rfidCode: "",
    });
    setSuccessMessage("");
    setErrorMessage("");
  };

  if (loading) {
    return (
      <div className="seat-allotment-container">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading students...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="seat-allotment-container">
      <div className="seat-allotment-header">
        <h1>Seat and RFID Allotment</h1>
        <p className="subtitle">
          Assign seats and RFID codes to registered students
        </p>
      </div>

      {successMessage && (
        <div className="alert alert-success">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 12l2 2 4-4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-error">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M12 8v4M12 16h.01"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          {errorMessage}
        </div>
      )}

      <div className="seat-allotment-content">
        <div className="allotment-form-card">
          <h2>Allot Seat and RFID</h2>
          <form onSubmit={handleSubmit} className="allotment-form">
            <div className="form-group">
              <label htmlFor="studentId" className="form-label">
                Select Student <span className="required">*</span>
              </label>
              <select
                id="studentId"
                name="studentId"
                value={formData.studentId}
                onChange={handleStudentSelect}
                className={`form-input ${errors.studentId ? "error" : ""}`}
                disabled={submitting}
              >
                <option value="">-- Select a student --</option>
                {unallottedStudents.length === 0 ? (
                  <option value="" disabled>
                    No unallotted students available
                  </option>
                ) : (
                  unallottedStudents.map((student, index) => (
                    <option
                      key={`${student.emailId}-${index}`}
                      value={`${student.firstName} ${student.lastName} (${student.emailId})`}
                    >
                      {student.firstName} {student.lastName} - {student.emailId}
                    </option>
                  ))
                )}
              </select>
              {errors.studentId && (
                <span className="error-message">{errors.studentId}</span>
              )}
              {unallottedStudents.length > 0 && (
                <span className="form-hint">
                  {unallottedStudents.length} student(s) available for allotment
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="seatNumber" className="form-label">
                Seat Number <span className="required">*</span>
              </label>
              <select
                id="seatNumber"
                name="seatNumber"
                value={formData.seatNumber}
                onChange={handleInputChange}
                className={`form-input ${errors.seatNumber ? "error" : ""}`}
                disabled={submitting}
              >
                <option value="">-- Select a seat --</option>
                {listUnAllocatedSeat.map((seatNum) => (
                  <option
                    key={seatNum.seatAllotmentId}
                    value={seatNum.seatNumber}
                  >
                    {seatNum.seatNumber}
                  </option>
                ))}
              </select>
              {errors.seatNumber && (
                <span className="error-message">{errors.seatNumber}</span>
              )}
              <span className="form-hint">
                {listUnAllocatedSeat.length} seat(s) available out of 120 total
                seats
                {getOccupiedSeats().length > 0 && (
                  <span> • {getOccupiedSeats().length} seat(s) occupied</span>
                )}
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="rfidCode" className="form-label">
                RFID Code <span className="required">*</span>
                {wsConnected && (
                  <span
                    className="ws-status"
                    style={{
                      marginLeft: "8px",
                      fontSize: "0.85em",
                      color: "#10b981",
                      fontWeight: "500",
                    }}
                  >
                    ● Connected
                  </span>
                )}
              </label>
              <input
                type="text"
                id="rfidCode"
                name="rfidCode"
                value={formData.rfidCode}
                onChange={handleInputChange}
                className={`form-input ${errors.rfidCode ? "error" : ""}`}
                placeholder={
                  wsConnected
                    ? "RFID will be auto-filled from reader"
                    : "Enter RFID code"
                }
                disabled={submitting}
              />
              {errors.rfidCode && (
                <span className="error-message">{errors.rfidCode}</span>
              )}
              <span className="form-hint">
                {wsConnected
                  ? "RFID reader connected - scan a card to auto-fill"
                  : "Enter the unique RFID identification code or wait for reader connection"}
              </span>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleReset}
                className="btn btn-secondary"
                disabled={submitting}
              >
                Reset
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || unallottedStudents.length === 0}
              >
                {submitting ? (
                  <>
                    <div className="btn-spinner"></div>
                    Allotting...
                  </>
                ) : (
                  "Allot Seat & RFID"
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="allotment-stats-card">
          <h3>Allotment Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{unallottedStudents.length}</div>
              <div className="stat-label">Unallotted Students</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{allottedStudents.length}</div>
              <div className="stat-label">Allotted Students</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {unallottedStudents.length + allottedStudents.length}
              </div>
              <div className="stat-label">Total Students</div>
            </div>
          </div>
          <button
            onClick={fetchStudents}
            className="btn btn-outline"
            style={{ marginTop: "16px", width: "100%" }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 4v6h6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M23 20v-6h-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Refresh Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeatAllotment;
